import * as xmpp from '../../global/xmppPayloadHelper'
import { postGroupMessage, getGroupChatHistory } from './groups'
import { postMessage, getChatHistory } from './conversations'
import { send, sendIQ } from './serverConnection'
import { Chat } from '../../global/chatSettingsConfig'
import chatLog from '../../global/chat-log'
import { getIsLowBandwidth } from '../../hooks/useIsLowBandwidth'
import orchestrationApi from '../orchestration'

/*
  Enable message carbons. This allows messages to be delivered to
  all my logged-in devices.
  https://xmpp.org/extensions/xep-0280.html#example-10
  Example response for a chat message:
  <message from="<jid>" to="<jid>/<resource_location>" type="chat" xmlns="jabber:client">
  <sent xmlns="urn:xmpp:carbons:2">
    <forwarded xmlns="urn:xmpp:forward:0">
      <message from="<jid>/<another_location>" to="<toJid>" type="chat" xml:lang="en" xmlns="jabber:client">
        <body>the message is here</body>
      </message>
    </forwarded>
  </sent>
</message>
*/
export const enableMessageCarbons = ({ connection, fromJid }) => {
  chatLog('[Messages] Enabling message carbons on the server')
  const stanza = xmpp.payloadToConfigureMessageCarbons({ fromJid })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

/**
 * Send a message. Required to specify which entity will receive the message, a person or a group.
 * @param {isGroup} isGroup True to send it a group, false to send it to a person
 * @param {connection} connection The chat server connection
 * @param {fromJid} fromJid The person logged in
 * @param {toJid} toJid The person or group
 * @param {message} message The message text to be sent
 * @param {fromDisplayName} message The name of the person sending the message
 * @param {fromResource} message The resource (device assigned id) sending the message
 */
export const sendMessage = ({ connection, fromJid, toJid, isGroup, message, fromDisplayName, fromResource }) => {
  if (isGroup === null || isGroup === undefined) {
    throw new Error(`Unable to determine who should receive this message: ${message}`)
  }

  // Fire and forget this log metric
  try {
    orchestrationApi.chatmetrics.logSendMessage({
      toJid,
      fromJid,
      jabberResource: fromResource,
      fromDisplayName,
      message,
    })
  } catch (e) {
    // ignoring
  }

  if (isGroup) {
    return postGroupMessage({ chatRoom: toJid, fromJid, fromResource, message, fromDisplayName, connection })
  }
  return postMessage({ toJid, fromJid, fromResource, message, fromDisplayName, connection })
}

/*
  Let others know you are typing.
*/
export const sendComposingChatState = ({ toJid, fromJid, isGroup, connection }) => {
  chatLog(`[ChatState] Sending compose state`)
  const stanza = xmpp.payloadToSetChatState({ toJid, fromJid, isGroup, chatState: Chat.ChatState.COMPOSING })
  return send({ connection, stanza })
}

/*
  Let others know you paused typing.
*/
export const sendPausedChatState = ({ toJid, fromJid, isGroup, connection }) => {
  chatLog(`[ChatState] Sending paused state`)
  const stanza = xmpp.payloadToSetChatState({ toJid, fromJid, isGroup, chatState: Chat.ChatState.PAUSED })
  return send({ connection, stanza })
}

/**
 * Get chat history. Required to specify which entity will receive the message, a person or a group.
 * @param {isGroup} isGroup True to get group history, false to get person history
 * @param {connection} connection The chat server connection
 * @param {fromJid} fromJid The person logged in
 * @param {toJid} toJid The person or group
 * @param {paginationToken} paginationToken The paginationToken, which is the oldest messages received in the previous request.
 */
export const getMessagesHistory = ({ connection, fromJid, toJid, isGroup, paginationToken }) => {
  const count = getIsLowBandwidth()
    ? Chat.Settings.DEFAULT_LOW_BANDWIDTH_CHAT_HISTORY_COUNT
    : Chat.Settings.DEFAULT_CHAT_HISTORY_COUNT
  if (isGroup === null || isGroup === undefined) {
    throw new Error(`Unable to determine who which messages to retrieve.`)
  }
  if (isGroup) {
    return getGroupChatHistory({ chatRoom: toJid, connection, paginationToken, count })
  }

  return getChatHistory({ toJid, fromJid, connection, paginationToken, count })
}
